import React from "react";
import {Alert, Button, Col, Row, Input, Card, Modal, notification, Tooltip, Divider, Select, Dropdown} from "antd";
import {EditableRow} from "./editableRow";
import {EditableCell} from "./editableCell";
import {
    CaretRightOutlined, DownOutlined,
    InfoCircleOutlined,
    LeftCircleTwoTone, LockOutlined,
    RightCircleTwoTone, UnlockOutlined,
    WarningOutlined
} from "@ant-design/icons";
import {withRouter} from "react-router-dom";
import {API} from "../../api";
import {
    handleFhirValidation,
    handleError,
    isAdmin,
    getGroovyFuncs,
    _getMappingData,
    getStickyDropdownSelection,
    setStickyDropdownSelection,
    getGroovyFuncNamesFromStr,
    isCode,
    checkPermission
} from "../../utils/util";

import { expand } from "../../utils/pathExpander";

import {
    JSON_VIEW,
    QA_VIEW_SELECT,
    TABLE_TOOLTIP_DRIVING,
    TABLE_TOOLTIP_PATH,
    TABLE_TOOLTIP_VALUE,
    TABLE_TOOLTIP_FLAG,
    USER_ROLE
} from "../../consts";
import DataSourceTextArea from "../dataSources/dataSourceTextArea";
import ValidationMessages from "../validationMessages";
import QAViewWrapper from "../dataQA/QAViewWrapper";
import TableHeader from "./tableHeader";
import {isEmpty, isEqual} from "lodash";
import {createJSONtoFHIRRequestBody} from "./editableTable.util";
import {VirtualTable} from "../virtualTable";
import {DropdownMenu} from "./dropdownMenu";
import { DimaContext } from "../../DimaContext";

const {Option} = Select;
const {confirm} = Modal;

export const EditableContext = React.createContext(null);

class EditableTable extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            client: props.client,
            fileId: null,
            resourcetype: getStickyDropdownSelection(props.client.id, 'resourcetype', 'Patient'),
            namespace: getStickyDropdownSelection(props.client.id, 'namespace', 'default'),
            file: getStickyDropdownSelection(props.client.id, 'file', ''),
            version: getStickyDropdownSelection(props.client.id, 'version', 'latest'),
            profile_url: '',
            resourcetypeNamespaceFileOptions: null,
            versions: [],
            file_column: null,
            dataSource: [],
            fhirPreview: {},
            fhirErrors: {},
            groovyError: null,
            previewLoading: false,
            validateLoading: false,
            parseError: false,
            sampleData: null,
            engineType: "JSON",
            loading: false,
            loadingSaveVersion: false,
            allowTableEdits: true,
            mappingSearchText: '',
            dataSourceVisible: [],
            newVersion: '',
            undoCellVersionHistory: [],
            redoCellVersionHistory: [],
            structuredefElements: [],
            hotKeyInAction: '',
            selectOptions: [],
            namespaceOptions: [],
            versionOptions: [],
            byResourcetype: true,
            searchText: '',
            searchedColumn: '',
            wideView: false,
            currentPath: '',
            currentValue: '',
            groovyFunctions: [],
            sampleDataColumns: [],
            sampleDataValue: null,
            groovyOutputs: [],
            mappingReview: undefined,
            showSaveVersionModal: false,
            selectedFilters: [],
            tableColumns: [
                {
                    title: <><Tooltip placement="bottom"
                                      title={TABLE_TOOLTIP_FLAG}>
                        <InfoCircleOutlined/> Flag
                    </Tooltip></>,
                    dataIndex: 'flag_content',
                    width: 25,
                    sorter: (a, b) => {
                        const flagA = a.flag_content ? 1 : 0;
                        const flagB = b.flag_content ? 1 : 0;
                        return flagA - flagB;
                    }
                },
                {
                    title: <><Tooltip placement="bottom"
                                      title={TABLE_TOOLTIP_PATH}>
                        <InfoCircleOutlined/> Path
                    </Tooltip></>,
                    dataIndex: 'path',
                    width: 187.5,
                    defaultSortOrder: "ascend",
                    sorter: (a, b) => a.path.localeCompare(b.path)
                },
                {
                    title: <><Tooltip placement="bottom"
                                      overlayClassName="extra-wide"
                                      title={TABLE_TOOLTIP_VALUE}>
                        <InfoCircleOutlined/> Value
                    </Tooltip></>,
                    dataIndex: 'value',
                    width: 187.5,
                    sorter: (a, b) => {
                        if (a.value === null || b.value === null) return 1;
                        return a.value.localeCompare(b.value)
                    }
                },
                {
                    title: <><Tooltip placement="bottom"
                                      overlayClassName="extra-wide"
                                      title={TABLE_TOOLTIP_DRIVING}>
                        <InfoCircleOutlined/> Driving
                    </Tooltip></>,
                    dataIndex: 'driving',
                    width: 50
                },
                {
                    title: 'Action',
                    dataIndex: null,
                    width: 50
                },
            ]
        };
    }

    componentDidMount = () => {
        this.getData();
        // For the initial rollout of the staging feature, edits to 'latest' will be locked using the lock button on the page,
        // which will remain available and allow users with edit permissions to make edits to 'latest'.
        // Following the initial rollout, we will lock edits to 'latest' behind permissions.
        if (process.env.REACT_APP_FEATURE_MAP_STAGING && this.state.version === 'latest') {
            this.setState({allowTableEdits: false});
        }
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (
            (process.env.REACT_APP_FEATURE_MAP_STAGING && (prevState.version !== this.state.version))
         ) {
            if ((this.state.version === 'latest' && this.state.allowTableEdits) || (prevState.version === 'latest' && !this.state.allowTableEdits)) {
                this.toggleTableEdits();
            }
        }
    }

    // TODO: use Promise.all
    getData = async () => {
        this.setState({loading: true})
        API.get('mapping/resourcetypes', {params: {client_id: this.state.client.id, active: true}}).then(res => {
            this.setState({selectOptions: res.data})
        }).catch((err) => handleError(err))

        const groovyFunctions = await getGroovyFuncs({ client_id: this.state.client.id});
        this.setState({
            groovyFunctions: groovyFunctions.map((gf) =>
                getGroovyFuncNamesFromStr(gf.value, true))
        })

        await this.getSampleData();

        await this.getDistinctVersions();
        await this.getAutoCompleteOptions(groovyFunctions);

        await this.getMapData().then(() => {
        const profileUrl = this.getProfile();
        API.get(`/structuredef?profile_url=${profileUrl}`).then(res => {
            this.setState({
                structuredefElements: res.data.elements,
                profile_url: profileUrl,
            });
        });
    })
    .catch(err => {
      handleError(err);
    })
    .finally(() => {
        this.setState({ loading: false });
        setStickyDropdownSelection(this.state.client.id, {
            resourcetype: this.state.resourcetype,
            namespace: this.state.namespace,
            file: this.state.file,
            version: this.state.version,
        });
        });
    };

    getProfile = () => {
        if (this.state.dataSource == null) {
            return ""
        }

        let profile = this.state.dataSource.filter(mapping => {
            return mapping.path != null && mapping.path.includes('meta.profile[');
        })

        if (profile.length > 0) {
            return profile[0].value.replace('"', '').replace('"', '').split('|')[0]
        } else {
            return ""
        }
    }

    /**
     * Get distinct versions for a resourcetype/namespace/file combination
     */
    getDistinctVersions = async () => {
        const res = await API.get("/meta/distinct/version", {
            params: {
                client_id: this.state.client.id,
                resourcetype: this.state.resourcetype,
                namespace: this.state.namespace,
                file: this.state.file,
            }
        });
        this.setState({versionOptions: res.data['version']}
            , () => {
            if (res.data['version'].length > 0 && !res.data['version'].includes(this.state.version)) {
                this.setState({version: res.data['version'][0]}, () => {});
            }
        });
    }

    showSaveSampleConfirm = () => {
        confirm({
            title: `Are you sure?`,
            icon: <WarningOutlined/>,
            content: <Alert message="Do not upload any PHI or any sensitive data" type="error" />,
            okText: `Save Sample Data`,
            okType: 'primary',
            cancelText: 'Cancel',
            okButtonProps: {"data-cy": "save-sampledata-confirm-btn"},
            onOk: () => {
                const {sampleData, fileId} = this.state;

                return API.put(`/file/${fileId}`, {sampleData}).then(() => {
                    notification.success({
                        message: 'Success',
                        description: 'Saved sample data.'
                    });
                }).catch(err => handleError(err))
            }
        });
    }

    handleDelete = record => {
      const dataSource = [...this.state.dataSource];
      API.put(`/mapping/${record.id}`, { deleted: true })
        .then(res => {
          notification.success({
            description: `Deleted (${res.status})`,
            duration: 2,
          });
          const oldRow = record;
          const newRow = JSON.parse(JSON.stringify(record));
          newRow.deleted = true;
          oldRow.deleted = false;
          const historyObject = {
            oldRow,
            newRow,
          };
  
          this.setState(
            {
              dataSource: dataSource.filter(item => item.id !== record.id),
            },
            () => {
              this.updateCellVersionState('', historyObject);
            },
          );
        })
        .catch(err => handleError(err));
    };

    handleSave = async (row) => {
        if (row.value) {
            row.value = row.value.trim();
        }

        if (row.path) {
            row.path = row.path.trim();
        }

        const oldData = this.state.dataSource;
        const newData = [...this.state.dataSource];
        const index = newData.findIndex(item => row.id === item.id);
        const groovyFunctions = getGroovyFuncNamesFromStr(row.value);
        const nonExistantGroovyFunctions = groovyFunctions ? groovyFunctions.filter(x => !this.state.groovyFunctions.includes(x)) : [];
        if (nonExistantGroovyFunctions.length) {
            notification.error({
                description: `The Following Groovy Functions Do Not Exist: ${nonExistantGroovyFunctions.join(', ')} `,
                duration: 3,
            })
        }

        let item = newData[index];
        if (index === -1 || row.deleted === true) {
          item = row;
        }

        if (index >= 0) {
            if (item.deleted) {
                newData.splice(index, 1);
            } else if (isEqual(item, row)) {
                return;
            } else {
                newData.splice(index, 1, {...item, ...row});
            }
        } else {
            newData.unshift(item);
        }

        this.setState({dataSource: newData}, () => {
            API.put(`/mapping/${item.id}`, row)
            .then(res => {
                notification.success({
                    description: `Updated (${res.status})`,
                    duration: 2,
                });

                if (res.status === 200) {
                    const historyObject = {
                        oldRow: item,
                        newRow: row,
                    };
                    this.updateCellVersionState(this.state.hotKeyInAction, historyObject);
                    this.getMapData();
                }
            })
            .catch(err => {
                this.setState({ dataSource: oldData });
                handleError(err);
            });
        });
    };

    getMapData = async ()=> {
      _getMappingData(
        this.state.client.id,
        this.state.version,
        this.state.resourcetype,
        this.state.namespace,
        this.state.file,
      ).then(res => {
        const file = (res.data.mappings[0] && res.data.mappings[0].file) || this.state.file;
        this.setState({
          dataSource: res.data.mappings.filter(item => ((item.deleted === false || item.deleted === null) && item.file === file)),
          file: file
        });
      });
    }

    /**
     * Based on the action ('', undo, redo), update undo/redo history array states
     * @param {*} action
     * @param {*} historyObject
     */
    updateCellVersionState = (action, historyObject) => {
        if (action === '') {
            // if the change is manual/new
            this.setState(prevState => ({
                undoCellVersionHistory: [historyObject, ...prevState.undoCellVersionHistory],
            }));
        } else if (action === 'undo') {
            // If the user uses undo key (command + z / control + z)
            let undoneData = this.state.undoCellVersionHistory[0]
            this.setState(prevState => ({
                undoCellVersionHistory: this.state.undoCellVersionHistory.slice(1),
                redoCellVersionHistory: [undoneData, ...prevState.redoCellVersionHistory],
                hotKeyInAction: ''
            }));
        } else if (action === 'redo') {
            // If the user uses redo key (command + shift + z / control + shift + z)
            let redoneData = this.state.redoCellVersionHistory[0]
            this.setState(prevState => ({
                undoCellVersionHistory: [redoneData, ...prevState.undoCellVersionHistory],
                redoCellVersionHistory: this.state.redoCellVersionHistory.slice(1),
                hotKeyInAction: ''
            }));
        }
        this.handleHistorySize();
    }

    /**
     * Maintain size of redo history state array to 3 and undo history state array to 20
     */
    handleHistorySize = () => {
        if (this.state.undoCellVersionHistory.length > 20) {
            this.setState(prevState => ({
                undoCellVersionHistory: [...prevState.undoCellVersionHistory.slice(0, 20)]
            }));
        }
        if (this.state.redoCellVersionHistory.length > 20) {
            this.setState(prevState => ({
                redoCellVersionHistory: [...prevState.redoCellVersionHistory.slice(0, 20)]
            }));
        }
    }

    /**
     * Triggered when user uses command + z or control + z key combinations to undo
     */
    undo = () => {
      if (this.state.undoCellVersionHistory.length > 0) {
        this.setState({ hotKeyInAction: 'undo' });
        const undoData = this.state.undoCellVersionHistory[0].oldRow;
        this.handleSave(undoData);
      }
    };
  
    /**
     * Triggered when user uses command + shift + z or control + shift + z key combinations to redo
     */
    redo = () => {
      if (this.state.redoCellVersionHistory.length > 0) {
        this.setState({ hotKeyInAction: 'redo' });
        const redoData = this.state.redoCellVersionHistory[0].newRow;
        this.handleSave(redoData);
      }
    };

    onChangeSampleData = (e) => {
        let sampleData;
        try {
            sampleData = JSON.parse(e.target.value);
            this.setState({
                parseError: false,
                sampleData: sampleData,
                sampleDataValue: JSON.stringify(sampleData, null, 2)
            })
        } catch (error) {
            sampleData = e.target.value;
            this.setState({
                parseError: true,
                sampleData: null,
                sampleDataValue: sampleData
            })
        }
    }


    /**
     * Finds all mappings that will need to be evaluated (the ones that contain code) and 
     * generates params for evaluate request 
     * @param {*} mappings 
     * @param {*} sampleData 
     * @param {*} groovyFuncs 
     * @param {*} clientId 
     */
    getGroovyValues = async (mappings, sampleData, groovyFuncs, clientId) => {
        try {
            const groovyMappings = mappings.filter((m) => isCode(m.value, m.path));
            let groovyStrFunctions = [];
            
            groovyMappings.forEach((
                {
                    value,
                    path
                }
            ) => {
                const pathList = path.split('.');
                groovyStrFunctions = groovyStrFunctions.concat(expand(pathList, value, {sampleData, path}));
            })

            if(!groovyStrFunctions.length) {
                this.setState({groovyOutputs: []});
                return;
            }

            const params = {
                mappings: groovyStrFunctions,
                groovy: groovyFuncs.filter((gf) => gf.value_type === 'def').map((gf) => gf.value),
                client_id: clientId
            }
            // send request to evaluate groovy
            const res = await API.post('stdlib/execute', params);
            const data = JSON.parse(res.data)
            const groovyMessage = JSON.parse(data.body).message;
            if(data.statusCode !== 200) {
                this.setState({groovyError: groovyMessage})
            } else {
                this.setState({groovyOutputs: JSON.parse(groovyMessage)})
            } 
        } catch (error) {
            this.setState({groovyOutputs: []})
            notification.warning({description: "Failed to validate groovy mappings"})
        }
    }

    generateFHIRPreview = async (validate) => {
        if (!this.state.sampleData) {
            notification.error({description: "Sample data missing or malformed"})
            return
        }

        if (Array.isArray(this.state.sampleData)) {
            return notification.error({description: "Error: sample data must be a single JSON object (cannot be array)"})
        }

        let resourceTypeMapping = this.state.dataSource.filter(mapping => mapping.path === 'resourceType');
        if (resourceTypeMapping === undefined || resourceTypeMapping.length !== 1) {
            notification.error({
                description: <>Mapping is missing <code>resourceType</code></>,
                duration: 0
            })
            return
        }
        let resourceType = resourceTypeMapping[0].value.replace(/"/g, '').replace(/'/g, '').replace(/ /g, '');

        this.setState({
            fhirPreview: {"loading": true},
            fhirErrors: {"loading": true},
            previewLoading: true,
            validateLoading: true,
            groovyOutputs: [],
            groovyError: null,
        })

        const groovyFuncs = await getGroovyFuncs({client_id: this.state.client.id});

        this.getGroovyValues(this.state.dataSource, this.state.sampleData, groovyFuncs, this.state.client.id);

        let reqBody = await createJSONtoFHIRRequestBody(this.state.dataSource, this.state.client.id, this.state.sampleData, false, this.state.file, this.state.engineType, groovyFuncs)
        
        API.post('/preview', reqBody).then(res => {
            let fhirPreview = res.data._DATA[0];
            if (isEmpty(fhirPreview)) {
                notification.warning({
                    description: `FHIR preview is empty. Please check mappings for driving column set too high 
                        (e.g. parent:4) and other errors.`,
                    duration: 0
                })
            }
            this.setState({
                fhirPreview: fhirPreview,
                previewLoading: false,
                validateLoading: false,
            }, () => {
                if (validate && !isEmpty(fhirPreview)) {
                    this.handleValidate(resourceType)
                }
            })
            notification.success({
                message: "Success",
                description: `Generated preview (${res.status})`
            })
        }).catch(err => {
            handleError(err)
            this.setState({
                fhirPreview: {},
                previewLoading: false,
                validateLoading: false,
            })
        })
    }

    handleValidate = (resourceType) => {
        this.setState({
            validateLoading: true,
            previewLoading: true
        })

        API.post(`/validate/fhir?resourcetype=${resourceType}`, this.state.fhirPreview).then(res => {
            this.setState({fhirErrors: handleFhirValidation(res.data, this.state.fhirPreview)})
        }).catch(err => handleError(err)
        ).finally(() => {
            this.setState({
                validateLoading: false,
                previewLoading: false
            })
        });
    }

    /**
     * Callback function for Mappings search / filter.
     */
    searchMappings = (e) => {
        this.setState({mappingSearchText: e.target.value});
    }

    togglePreviewViewWidth = () => this.setState({wideView: !this.state.wideView})

    setParentState = (obj, callback) => this.setState(obj, callback)

    handleEngineTypeChange = (engineType) => this.setState({engineType})

    toggleTableEdits = () => this.setState({allowTableEdits: !this.state.allowTableEdits})

    /**
     * Set version
     * @param resourceInput
     */
    handleDropdownSelect = (resourceInput) => {
        let selectedOption = this.state.versionOptions.filter(res => res === resourceInput.key)[0];
        this.setState({version: selectedOption, undoCellVersionHistory: [], redoCellVersionHistory: []}, () => this.getData())
    }

    /**
     * Set filter
     * @param filters - Array of filter values
     */
    handleFilterSelect = (filters) => {
        this.setState({selectedFilters: filters});
    }

    /**
     * Set the file that is being mapped. Also empties the cell version history arrays to avoid changing
     * mappings that are not currently displayed to the user.
     */
    updateSelectedMapping = (selectedArr) => {
        if (selectedArr !== undefined && selectedArr.length > 0) {
            let resourcetype, namespace, file;
            if (this.state.byResourcetype) {
                resourcetype = selectedArr[0];
                namespace = selectedArr[1];
                file = selectedArr[2];
            } else {
                resourcetype = selectedArr[1];
                namespace = selectedArr[2];
                file = selectedArr[0];
            }

            this.setState({
                resourcetype: resourcetype,
                namespace: namespace,
                file: file,
                undoCellVersionHistory: [],
                redoCellVersionHistory: []
            }, () => {
                this.getData();
            })
        }
    }

    /**
     * Get and set AutoComplete options. The options given are:
     * 1) columns present in the sample data, prefixed with a _.
     * 2) the groovy function library, pulled from the stdlib table in the db
     * 3) true and false values
     * 4) reference paths should pull values from the id columns on other resourceTypes,
     *    prefixed with [resourceType] i.e. "Patient/" + _.patientid"
     */
    getAutoCompleteOptions = async (groovyFunctions) => {
        let suggestionOptions = [
            {
                label: 'Booleans',
                options: [
                    {
                        label: 'true',
                        value: 'true'
                    },
                    {
                        label: 'false',
                        value: 'false'
                    }
                ]
            }
        ];
        let functionOptions = {
            label: 'Functions',
            options: []
        }
        groovyFunctions.forEach((f) => {
            let newSuggestion = f.value.replace('static def ', '');
            newSuggestion = newSuggestion.substring(0, newSuggestion.indexOf(' {'));
            functionOptions['options'].push(
                {
                    label: newSuggestion,
                    value: newSuggestion
                }
            )
        })
        suggestionOptions.push(functionOptions);

        // Maps array to data format suitable for auto complete
        let mapFnc = (d) => {
            return {'label': '_.' + d, 'value': '_.' + d}
        }
        suggestionOptions.push({
            label: 'Columns',
            options: this.state.sampleDataColumns.map(mapFnc)
        })

        this.setState({suggestions: suggestionOptions})
    }

    /**
     * Set column headers based on the first row in the JSON that we
     * get from S3.
     */
    setColumnHeaders = (sampleJson) => {
        const sampleDataColumns = Object.keys(sampleJson[0]);
        this.setState({
            sampleDataColumns: sampleDataColumns
        })
    }

    setSampleDataRows = (sampleJson) => {
        const sampleDataRows = []
        for (let i = 0; i < sampleJson.length; i++) {
            let newRow = {}
            Object.keys(sampleJson[i]).forEach(
                (k) => {
                    newRow[k] = sampleJson[i][k]
                }
            )
            newRow['key'] = i
            sampleDataRows.push(newRow)
        }
        this.setState({
            sampleDataRows: sampleDataRows
        })
    }

    getMappingReview = async (fileId) => {
        if(fileId) {
            const res = await API.get(`/mapping/review?fileId=${fileId}&version=${this.state.version}&resourcetype=${this.state.resourcetype}&namespace=${this.state.namespace}`);
            const { mappingReview } = res.data;
            if(mappingReview) {
                this.setState({ mappingReview });
            } else {
                this.setState({ mappingReview: null });
            }
        } else {
            this.setState({ mappingReview: null });
        }
    }

    /**
     * Get sample data column names
     * TODO: refactor (move into utils)
     */
    getSampleData = async () => {
        let fileData = await API.get("/file", {
            params: {
                client_id: this.state.client.id,
                name: this.state.file,
                archived: false
            }
        });
        if (fileData.data.files.length === 0) {
            this.setState({
                sampleDataColumns: [],
                sampleDataRows: [],
                sampleDataValue: null,
                sampleData: null
            })
            return;
        }
        const fileId = fileData.data.files[0].id;
        this.setState({fileId}, () => this.getMappingReview(fileId))
        await API.get(`/file/${fileId}/raw`).then(
            res => {
                let responseSampleData = res.data.sampleData
                if (!Array.isArray(responseSampleData)) responseSampleData = [responseSampleData]
                if (responseSampleData.length > 0) {
                    this.setColumnHeaders(responseSampleData);
                    this.setSampleDataRows(responseSampleData);
                }
                if (fileData.data.files[0].sampleData) {
                    this.setState({
                        parseError: false,
                        sampleDataColumns: [],
                        sampleDataRows: [],
                        sampleDataValue: JSON.stringify(fileData.data.files[0].sampleData, null, 2),
                        sampleData: fileData.data.files[0].sampleData,
                        
                    });
                } else if (responseSampleData.length > 0) {
                    this.setState({
                        parseError: false,
                        sampleData: responseSampleData[0],
                        sampleDataValue: JSON.stringify(responseSampleData[0], null, 2),
                    });
                } else {
                    this.setState({
                        sampleDataColumns: [],
                        sampleDataRows: [],
                        sampleDataValue: null,
                        sampleData: null,
                    });
                }
                this.setState({loading: false});
            }
        ).catch((res) => {
            if (fileData.data.files[0].sampleData) {
                this.setState({
                    sampleDataColumns: [],
                    sampleDataRows: [],
                    sampleDataValue: JSON.stringify(fileData.data.files[0].sampleData, null, 2),
                    sampleData: fileData.data.files[0].sampleData
                });
            }
            notification.error({
                description: `(${res.status})`,
                duration: 2,
            });
        })
    }

    /**
     * Get all current mappings and save new version.
     * Update table to display new version
     * @returns {Promise<void>}
     */
    handleSaveVersion = async () => {
        this.setState({ saveVersionLoading: true });

        if (this.state.dataSource.length === 0) {
            notification.error({
                description:
                'No mappings found. Please select a version, resourceType, namespace, and file ' +
                'with existing mappings.',
            });
        return;
        }

        if (this.state.newVersion.length <= 2) {
            notification.error({ description: 'Version name must be at least 3 characters long' });
            return;
        }

        const newVersionDataSource = { mappings: this.state.dataSource };

        newVersionDataSource.mappings.forEach(row => {
            row.version = this.state.newVersion;
        });

        API.post('/mapping', newVersionDataSource)
        .then(res => {
            if (res.status === 200) {
                notification.success({
                    message: 'Success',
                    description: `Added version (${res.status})`,
                });
            this.setState({ version: this.state.newVersion }, () => {
                this.getData();
            });
            }

            this.setState({ newVersion: '' });
            this.getData();
        })
        .catch(err => handleError(err))
        .finally(() => {
            this.setState({
                showSaveVersionModal: false,
                saveVersionLoading: false,
            });
        });
    };

    render() {
        const dataSourceVisible = this.state.dataSource.filter((item) => {
            return (item.path.includes(this.state.mappingSearchText) || (item.value != null && item.value.includes(this.state.mappingSearchText)))
        })
        .filter(item => {
            if (!this.state.selectedFilters.includes('flagged')) {
                return true;
            } else {
                return item.flag_content;
            }
        });

        const components = {
            body: {
                row: EditableRow,
                cell: EditableCell,
            },
        };

        const columns = this.state.tableColumns.map((col) => {
            if (!col.editable) {
                return col;
            }

            return {
                ...col,
                onCell: (record) => ({
                    record: record,
                    editable: col.editable,
                    dataIndex: col.dataIndex,
                    title: col.title,
                    handleSave: this.handleSave,
                }),
            };
        });

        let hasNoMappings = this.state.selectOptions.files != null && this.state.selectOptions.files.length === 0;
        const lockButtonStyle = {marginLeft: 10 };
        const canEditMapping = process.env.REACT_APP_FEATURE_PERMISSIONS_V2 ? (this.context && checkPermission(this.context.userData.permissions, 'editMapping')) : isAdmin(USER_ROLE);

        return (
            <>
                <TableHeader state={this.state}
                             client={this.state.client}
                             getData={this.getData}
                             hasNoMappings={hasNoMappings}
                             profile_url={this.state.profile_url}
                             structuredefElements={this.state.structuredefElements}
                             undo={this.undo}
                             redo={this.redo}
                             updateSelectedMapping={this.updateSelectedMapping}
                             setParentState={this.setParentState}
                             saveVersionLoading={this.state.saveVersionLoading}
                             showSaveVersionModal={this.state.showSaveVersionModal}
                             handleSaveVersion={this.handleSaveVersion}
                             getMappingReview={this.getMappingReview}
                />
                <Divider/>
                {
                    hasNoMappings &&
                    <>No mappings found</>
                }
                {
                    !hasNoMappings &&
                    <>
                        <Row justify="space-between">
                            <Col>
                                <Input data-cy="search-mappings-input" style={{width: '350px'}} addonBefore="Search:"
                                       onChange={this.searchMappings}
                                />
                            </Col>
                            <Col>
                                {
                                    process.env.REACT_APP_FEATURE_MAP_STAGING &&
                                    this.state.version === 'latest' &&
                                    <Button
                                        style={{marginRight: 10}}
                                        onClick={() => this.setState({ showSaveVersionModal: true })}
                                    >
                                        Create New Version
                                    </Button>
                                }
                                <Select
                                    mode='multiple'
                                    allowClear
                                    placeholder='Filter mappings'
                                    style={{width: '150px', marginLeft: '10px', marginRight: '10px',}}
                                    options={[{label: 'Flagged', value: 'flagged'}]}
                                    onChange={this.handleFilterSelect}
                                />
                                <Dropdown
                                    trigger={['click']}
                                    overlay={<DropdownMenu options={this.state.versionOptions}
                                                           handleMenuClick={(e) => this.handleDropdownSelect(e, "version")}/>}>
                                    <Button data-cy='select-version-button'>
                                        <b>Version: </b>{this.state.version} <DownOutlined/>
                                    </Button>
                                </Dropdown>
                                {
                                    canEditMapping &&
                                    <Button style={lockButtonStyle} type={this.state.allowTableEdits ? null : 'primary'}
                                            onClick={this.toggleTableEdits}>
                                        {this.state.allowTableEdits ? <LockOutlined/> : <UnlockOutlined/>}
                                    </Button>
                                }
                            </Col>
                        </Row>
                        <br/>
                        <VirtualTable
                            components={components}
                            loading={this.state.loading}
                            allowTableEdits={this.state.allowTableEdits}
                            pagination={{pageSize: 500, size: "small"}}
                            size="small"
                            rowClassName={() => 'editable-row'}
                            dataSource={dataSourceVisible}
                            columns={columns}
                            rowKey="id"
                            scroll={{x: 2000, y: 500}}
                            handleSave={this.handleSave}
                            handleDelete={this.handleDelete}
                            suggestions={this.state.suggestions}
                            getMapData={this.getMapData}
                        />
                        <Row>
                            <Col style={{height: "70px"}}>
                                <h1>Sample Data</h1>
                            </Col>
                        </Row>
                        <Row gutter={[16, 16]}>
                            <Col>
                                <Button
                                    loading={this.state.validateLoading || this.state.loading}
                                    onClick={() => this.generateFHIRPreview(true)}
                                    style={{marginRight: "2px"}}
                                    data-cy="generate-fhir-preview-btn"
                                    type="primary">
                                    <CaretRightOutlined/>
                                    Generate FHIR Preview & Validate
                                </Button>
                                <>
                                    <Tooltip placement="top"
                                             title="Select an engine type to use for JSON to FHIR transformation">
                                        <Select title="Engine" defaultValue={this.state.engineType}
                                                onChange={this.handleEngineTypeChange}>
                                            <Option value="JSON">
                                                JSON
                                            </Option>
                                            <Option value="TMSIS">TMSIS</Option>
                                        </Select>
                                    </Tooltip>
                                </>
                                <Button
                                    loading={this.state.validateLoading || this.state.loading}
                                    onClick={() => this.showSaveSampleConfirm()}
                                    style={{marginLeft: "2px"}}
                                    data-cy="save-sample-data-btn"
                                    type="primary"
                                >
                                    Save
                                </Button>
                            </Col>
                        </Row>
                        <br/>
                        <Row justify="start" align="top" gutter={[40, 16]}>
                            <Col span={this.state.wideView ? 2 : 6}>
                                <Card title={`${this.state.resourcetype} Sample Data`}
                                      extra={<span style={{cursor: "pointer"}}
                                                   onClick={() => this.togglePreviewViewWidth()}>
                                      {this.state.wideView ? <RightCircleTwoTone/> : <LeftCircleTwoTone/>}
                                  </span>}>
                                    <DataSourceTextArea
                                        id='editable-table-sample-data'
                                        sampleDataValue={this.state.sampleDataValue}
                                        parseError={this.state.parseError}
                                        onChangeSampleData={this.onChangeSampleData}
                                        wideView={this.state.wideView}
                                    />
                                    {
                                        this.state.parseError &&
                                        <>Error parsing JSON</>
                                    }
                                </Card>
                            </Col>
                            <Col span={this.state.wideView ? 14 : 10}>
                                <QAViewWrapper showPagination={false}
                                               key={Math.random()}
                                               title="FHIR Preview"
                                               defaultView={
                                                   !!localStorage.getItem(QA_VIEW_SELECT) ?
                                                       localStorage.getItem(QA_VIEW_SELECT) :
                                                       JSON_VIEW
                                               }
                                               resourcetype={this.state.resourcetype}
                                               namespace={this.state.namespace}
                                               version={this.state.version}
                                               client={this.state.client}
                                               sampleData={this.state.sampleData}
                                               resource={[{resource: this.state.fhirPreview}]}
                                               mappings={this.state.dataSource}
                                               reference={false} />
                            </Col>
                            <Col span={8}>
                                <ValidationMessages fhirErrors={this.state.fhirErrors}
                                                    sampleData={this.state.sampleData}
                                                    fhirPreview={this.state.fhirPreview}
                                                    mappings={this.state.dataSource}
                                                    groovyOutputs={this.state.groovyOutputs}
                                                    groovyError={this.state.groovyError}
                                                    />
                            </Col>
                        </Row>
                    </>
                }
            </>
        )
    }
}

EditableTable.contextType = DimaContext;

export default withRouter(EditableTable)